/**
 * ============================================================================
 * TERMIN-AGENT EMBEDDABLE CHAT WIDGET
 * High-End UI/UX für Handwerk, Praxen & KMU
 * ============================================================================
 */

// Inlined CSS if bundled via esbuild
declare const __INLINE_WIDGET_CSS__: string;

interface WidgetConfig {
  apiUrl?: string;
  tenantId?: string;
  businessName?: string;
  businessType?: string;
  emergencyNumber?: string;
  soundEnabled?: boolean;
  welcomeMessage?: string;
  quickReplies?: Array<{ label: string; value: string }>;
}

interface ChatMessageItem {
  id: string;
  role: 'user' | 'assistant' | 'model';
  content: string;
  timestamp: string;
  uiPayload?: any;
}

export class TerminAgentWidget {
  private config: WidgetConfig;
  private isOpen = false;
  private isPaused = false;
  private isSending = false;
  private conversationEnded = false;
  private readonly chatSessionId = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
  private pausedMessage = '';
  private welcomeMessage = '';
  private quickReplies: Array<{ label: string; value: string }> = [];
  private messages: ChatMessageItem[] = [];
  private attachedPhotoBase64: string | null = null;
  private soundEnabled = true;
  private slotDurationMinutes = 60;
  private slotCapacity = 1;
  private serviceLocationMode = 'mobile_service';
  private currentTheme: 'auto' | 'light' | 'dark' = 'auto';

  // DOM Elements
  private container!: HTMLElement;
  private fab!: HTMLElement;
  private chatWindow!: HTMLElement;
  private messagesBody!: HTMLElement;
  private inputField!: HTMLInputElement;
  private sendButton!: HTMLButtonElement;
  private emergencyBanner!: HTMLElement;
  private emergencyPhoneLink!: HTMLAnchorElement;
  private quickRepliesContainer!: HTMLElement;
  private photoPreviewBar!: HTMLElement;
  private photoThumbnail!: HTMLImageElement;
  private photoInfo!: HTMLElement;
  private fileInput!: HTMLInputElement;
  private voiceButton!: HTMLButtonElement;
  private speechRecognition: any = null;
  private isRecordingVoice = false;

  constructor(config: WidgetConfig = {}) {
    this.config = {
      apiUrl: config.apiUrl || this.detectApiUrl(),
      tenantId: config.tenantId || this.detectTenantId(),
      businessName: config.businessName || 'Meisterbetrieb & Praxiszentrum',
      businessType: config.businessType || 'handwerk',
      emergencyNumber: config.emergencyNumber || '+49 800 112 0000',
      soundEnabled: config.soundEnabled ?? true,
    };

    this.init();
  }

  private detectTenantId(): string {
    const script = document.currentScript as HTMLScriptElement | null;
    if (script && script.dataset.tenantId) {
      return script.dataset.tenantId;
    }
    const scriptTag = document.querySelector('script[data-tenant-id]') as HTMLScriptElement | null;
    if (scriptTag && scriptTag.dataset.tenantId) {
      return scriptTag.dataset.tenantId;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('tenantId')) {
        return params.get('tenantId')!;
      }
      if (params.get('tenant')) {
        return params.get('tenant')!;
      }
    } catch {
      // ignore
    }
    return 'default-tenant';
  }

  private detectApiUrl(): string {
    const script = document.currentScript as HTMLScriptElement | null;
    if (script && script.dataset.apiUrl) {
      return script.dataset.apiUrl;
    }
    return window.location.origin;
  }

  private detectTheme(): 'auto' | 'light' | 'dark' | null {
    try {
      const params = new URLSearchParams(window.location.search);
      const themeParam = params.get('theme') || params.get('ta-theme');
      if (themeParam && ['light', 'dark', 'auto'].includes(themeParam.toLowerCase())) {
        return themeParam.toLowerCase() as 'auto' | 'light' | 'dark';
      }
    } catch {}

    const script = document.currentScript as HTMLScriptElement | null;
    if (script && script.dataset.theme && ['light', 'dark', 'auto'].includes(script.dataset.theme.toLowerCase())) {
      return script.dataset.theme.toLowerCase() as 'auto' | 'light' | 'dark';
    }
    const scriptTag = document.querySelector('script[data-theme]') as HTMLScriptElement | null;
    if (scriptTag && scriptTag.dataset.theme && ['light', 'dark', 'auto'].includes(scriptTag.dataset.theme.toLowerCase())) {
      return scriptTag.dataset.theme.toLowerCase() as 'auto' | 'light' | 'dark';
    }

    return null;
  }

  public setTheme(theme: string): void {
    const normalized = (['light', 'dark', 'auto'].includes(theme) ? theme : 'auto') as 'auto' | 'light' | 'dark';
    this.currentTheme = normalized;

    if (this.container) {
      this.container.setAttribute('data-theme', normalized);
    }
    if (this.chatWindow) {
      this.chatWindow.setAttribute('data-theme', normalized);
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      if (normalized !== 'auto') {
        document.documentElement.setAttribute('data-ta-theme', normalized);
      } else {
        document.documentElement.removeAttribute('data-ta-theme');
      }
    }
  }

  public getTheme(): 'auto' | 'light' | 'dark' {
    return this.currentTheme;
  }

  private async init(): Promise<void> {
    this.injectStyles();
    this.createDomElements();
    this.setTheme(this.detectTheme() || 'auto');
    this.setupEventListeners();
    await this.fetchRemoteConfig();
    this.renderInitialMessage();
  }

  /**
   * CSS-Styles einfügen (entweder durch Inlined CSS aus dem Build oder via <link>)
   */
  private injectStyles(): void {
    if (document.getElementById('termin-agent-css')) return;

    if (typeof __INLINE_WIDGET_CSS__ !== 'undefined' && __INLINE_WIDGET_CSS__) {
      const style = document.createElement('style');
      style.id = 'termin-agent-css';
      style.textContent = __INLINE_WIDGET_CSS__;
      document.head.appendChild(style);
    } else {
      const link = document.createElement('link');
      link.id = 'termin-agent-css';
      link.rel = 'stylesheet';
      link.href = `${this.config.apiUrl}/widget.css?v=6`;
      document.head.appendChild(link);
    }
  }

  /**
   * Erstellt die DOM-Struktur des Widgets
   */
  private createDomElements(): void {
    this.container = document.createElement('div');
    this.container.id = 'termin-agent-root';

    this.container.innerHTML = `
      <!-- FAB -->
      <div id="termin-agent-fab" role="button" aria-label="Termin-Assistent öffnen">
        <div class="ta-fab-tooltip">Termin buchen & Sofort-Hilfe</div>
        <div class="ta-fab-button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <span class="ta-fab-badge-pulse"></span>
        </div>
      </div>

      <!-- Chat Window -->
      <div id="termin-agent-window" aria-hidden="true">
        <!-- Header -->
        <div class="ta-header">
          <div class="ta-header-info">
            <div class="ta-avatar">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span class="ta-status-dot"></span>
            </div>
            <div class="ta-header-text">
              <h3 id="ta-business-title">${this.escapeHtml(this.config.businessName || '')}</h3>
              <p id="ta-business-sub">
                <span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:#10b981;"></span>
                Online &bull; Antwortet sofort
              </p>
            </div>
          </div>
          <div class="ta-header-actions">
            <button class="ta-icon-btn" id="ta-sound-toggle" title="Ton umschalten">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
            </button>
            <button class="ta-icon-btn" id="ta-close-btn" title="Schließen">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
        </div>

        <!-- Notfall Banner -->
        <div class="ta-emergency-banner" id="ta-emergency-banner">
          <div class="ta-emergency-text">
            <strong>Akuter Notfall erkannt</strong><br>
            Sofortige Notdienst-Hilfe erforderlich.
          </div>
          <a class="ta-emergency-btn" id="ta-emergency-link" href="tel:${this.config.emergencyNumber}">
            Jetzt anrufen
          </a>
        </div>

        <!-- Chat Messages -->
        <div class="ta-messages-body" id="ta-messages-body"></div>

        <!-- Quick Replies -->
        <div class="ta-quick-replies" id="ta-quick-replies" style="display:none;"></div>

        <!-- Footer / Input -->
        <div class="ta-footer">
          <!-- Photo Preview Bar -->
          <div class="ta-photo-preview-bar" id="ta-photo-preview">
            <img class="ta-photo-thumbnail" id="ta-thumb" src="" alt="Schadensfoto" />
            <div class="ta-photo-info" id="ta-photo-name">Schadensfoto ausgewählt</div>
            <button class="ta-photo-remove-btn" id="ta-photo-remove" title="Foto entfernen">&times;</button>
          </div>

          <div class="ta-input-row">
            <label class="ta-upload-label" title="Foto vom Schaden anhängen">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
              <input type="file" id="ta-file-input" accept="image/*" style="display:none;" />
            </label>

            <input type="text" class="ta-input-field" id="ta-input" placeholder="Nachricht oder Anliegen schreiben..." autocomplete="off" />

            <button class="ta-voice-btn" id="ta-voice-btn" type="button" title="Spracheingabe / Diktieren">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
            </button>

            <button class="ta-send-btn" id="ta-send" title="Senden">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            </button>
          </div>
        </div>

        <!-- Powered by / White Label / Legal links -->
        <div class="ta-powered-by" id="ta-powered-by">
          <a href="https://bendig-design.de" target="_blank" rel="noopener">Termin-Agent</a>
          <span class="ta-legal-sep">&bull;</span>
          <a href="${this.config.apiUrl}/datenschutz" target="_blank" rel="noopener">Datenschutz</a>
          <span class="ta-legal-sep">&bull;</span>
          <a href="${this.config.apiUrl}/impressum" target="_blank" rel="noopener">Impressum</a>
        </div>
      </div>
    `;

    document.body.appendChild(this.container);

    // Grab elements
    this.fab = this.container.querySelector('#termin-agent-fab')!;
    this.chatWindow = this.container.querySelector('#termin-agent-window')!;
    this.messagesBody = this.container.querySelector('#ta-messages-body')!;
    this.inputField = this.container.querySelector('#ta-input')!;
    this.voiceButton = this.container.querySelector('#ta-voice-btn')!;
    this.sendButton = this.container.querySelector('#ta-send')!;
    this.emergencyBanner = this.container.querySelector('#ta-emergency-banner')!;
    this.emergencyPhoneLink = this.container.querySelector('#ta-emergency-link')!;
    this.quickRepliesContainer = this.container.querySelector('#ta-quick-replies')!;
    this.photoPreviewBar = this.container.querySelector('#ta-photo-preview')!;
    this.photoThumbnail = this.container.querySelector('#ta-thumb')!;
    this.photoInfo = this.container.querySelector('#ta-photo-name')!;
    this.fileInput = this.container.querySelector('#ta-file-input')!;
  }

  /**
   * Lädt Konfiguration vom Server
   */
  private async fetchRemoteConfig(): Promise<void> {
    try {
      const tenantParam = this.config.tenantId ? `?tenantId=${encodeURIComponent(this.config.tenantId)}` : '';
      const res = await fetch(`${this.config.apiUrl}/api/config${tenantParam}`);
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'paused' || data.paused) {
          this.isPaused = true;
          this.pausedMessage = data.pausedNotice || data.pausedMessage || 'Der digitale Terminassistent ist vorübergehend pausiert.';
          this.renderPausedState();
        }
        if (data.businessName) {
          this.config.businessName = data.businessName;
          const titleEl = this.container.querySelector('#ta-business-title');
          if (titleEl) titleEl.textContent = data.businessName;
        }
        if (data.emergencyNumber) {
          this.config.emergencyNumber = data.emergencyNumber;
          this.emergencyPhoneLink.href = `tel:${data.emergencyNumber}`;
        }
        if (data.welcomeMessage) {
          this.welcomeMessage = data.welcomeMessage;
        }
        if (data.slotDurationMinutes) {
          this.slotDurationMinutes = Number(data.slotDurationMinutes);
        }
        if (data.slotCapacity) {
          this.slotCapacity = Number(data.slotCapacity);
        }
        if (data.serviceLocationMode) {
          this.serviceLocationMode = data.serviceLocationMode;
        }
        if (data.widgetConfig) {
          this.applyWidgetConfig(data.widgetConfig);
        }
        if (data.quickReplies && Array.isArray(data.quickReplies)) {
          this.quickReplies = data.quickReplies;
          this.renderQuickReplies(data.quickReplies);
        } else {
          this.renderQuickReplies([
            { label: 'Notfall melden', value: 'Wasserrohrbruch / Akuter Notfall' },
            { label: 'Wartung', value: 'Wartung & Funktionsprüfung' },
            { label: 'Reparatur', value: 'Reparaturtermin anfragen' },
            { label: 'Beratung', value: 'Ich wünsche ein Beratungsgespräch' },
          ]);
        }
      }
    } catch {
      this.renderQuickReplies([
        { label: 'Notfall melden', value: 'Wasserrohrbruch / Akuter Notfall' },
        { label: 'Wartung', value: 'Wartung & Funktionsprüfung' },
        { label: 'Reparatur', value: 'Reparaturtermin anfragen' },
        { label: 'Beratung', value: 'Ich wünsche ein Beratungsgespräch' },
      ]);
    }
  }

  private renderQuickReplies(replies?: Array<{ label: string; value: string }>): void {
    if (!this.quickRepliesContainer) return;
    const list = replies || this.quickReplies;
    if (!list || list.length === 0) {
      this.quickRepliesContainer.style.display = 'none';
      return;
    }
    this.quickRepliesContainer.style.display = 'flex';
    this.quickRepliesContainer.innerHTML = list
      .map(
        (q) =>
          `<button class="ta-chip-btn" data-val="${this.escapeHtml(q.value)}">${this.escapeHtml(q.label)}</button>`
      )
      .join('');
  }

  /**
   * Wendet dynamische Design- und Modulkonfigurationen an
   */
  private applyWidgetConfig(cfg: any): void {
    if (!cfg) return;

    // 0. Farbmodus / Theme anwenden (light, dark oder auto)
    const activeTheme = this.detectTheme() || cfg.theme || 'auto';
    this.setTheme(activeTheme);

    // 1. Primär- & Akzentfarben dynamisch setzen
    const primaryColor = cfg.primaryColor || '#2563eb';
    const secondaryColor = cfg.secondaryColor || '#06b6d4';

    if (this.container) {
      this.container.style.setProperty('--ta-primary', primaryColor);
      this.container.style.setProperty('--ta-primary-hover', this.adjustColorBrightness(primaryColor, -15));
      this.container.style.setProperty('--ta-primary-light', this.hexToRgba(primaryColor, 0.12));
      this.container.style.setProperty('--ta-accent', secondaryColor);
      this.container.style.setProperty('--ta-accent-gradient', `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`);
      this.container.style.setProperty('--ta-shadow-glow', `0 10px 25px -5px ${this.hexToRgba(primaryColor, 0.45)}`);
    }

    // 2. Positionierung (bottom-right vs. bottom-left)
    if (cfg.position === 'bottom-left') {
      this.container.classList.add('ta-pos-left');
    } else {
      this.container.classList.remove('ta-pos-left');
    }

    // 3. Titel & Untertitel
    if (cfg.welcomeTitle) {
      const titleEl = this.container.querySelector('#ta-business-title');
      if (titleEl) titleEl.textContent = cfg.welcomeTitle;
    }
    if (cfg.welcomeSubtitle) {
      const subEl = this.container.querySelector('#ta-business-sub');
      if (subEl) {
        subEl.innerHTML = `<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:#10b981;"></span> ${this.escapeHtml(cfg.welcomeSubtitle)}`;
      }
    }

    // 4. Add-on Toggles
    const addons = cfg.addons || {};

    // Spracheingabe
    if (this.voiceButton) {
      this.voiceButton.style.display = addons.voiceInput === false ? 'none' : 'flex';
    }

    // Foto-/Datei-Upload
    const uploadLabel = this.container.querySelector('.ta-upload-label') as HTMLElement | null;
    if (uploadLabel) {
      uploadLabel.style.display = addons.fileUpload === false ? 'none' : 'flex';
    }

    // White-Labeling (Powered-by Badge)
    const poweredByEl = this.container.querySelector('#ta-powered-by') as HTMLElement | null;
    if (poweredByEl) {
      poweredByEl.style.display = addons.whiteLabel === true ? 'none' : 'block';
    }

    // 5. Logo / Avatar oben links
    const avatarEl = this.container.querySelector('.ta-avatar') as HTMLElement | null;
    if (avatarEl) {
      let logoUrl = cfg.logoUrl ? cfg.logoUrl.trim() : '';
      if (logoUrl && !logoUrl.startsWith('http://') && !logoUrl.startsWith('https://') && !logoUrl.startsWith('data:image/')) {
        const domain = logoUrl.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0].trim();
        if (domain.includes('.')) {
          logoUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;
        }
      }

      if (logoUrl) {
        avatarEl.classList.add('ta-has-logo');
        avatarEl.innerHTML = `
          <img src="${this.escapeHtml(logoUrl)}" class="ta-avatar-img" alt="Logo" onerror="this.style.display='none';this.parentElement.classList.remove('ta-has-logo');var s=this.nextElementSibling;if(s)s.style.display='block';">
          <svg style="display:none;" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <span class="ta-status-dot"></span>
        `;
      } else {
        avatarEl.classList.remove('ta-has-logo');
        avatarEl.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <span class="ta-status-dot"></span>
        `;
      }
    }
  }

  private hexToRgba(hex: string, alpha = 1): string {
    let c = hex.replace('#', '');
    if (c.length === 3) {
      c = c.split('').map((char) => char + char).join('');
    }
    const num = parseInt(c, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private adjustColorBrightness(hex: string, percent: number): string {
    let c = hex.replace('#', '');
    if (c.length === 3) {
      c = c.split('').map((char) => char + char).join('');
    }
    const num = parseInt(c, 16);
    let r = (num >> 16) + Math.round(255 * (percent / 100));
    let g = ((num >> 8) & 0x00ff) + Math.round(255 * (percent / 100));
    let b = (num & 0x0000ff) + Math.round(255 * (percent / 100));

    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  private renderPausedState(): void {
    const inputRow = this.container.querySelector('.ta-input-row') as HTMLElement;
    if (inputRow) {
      inputRow.style.opacity = '0.5';
      inputRow.style.pointerEvents = 'none';
    }
    if (this.inputField) {
      this.inputField.disabled = true;
      this.inputField.placeholder = 'Terminassistent derzeit pausiert';
    }
    const quickReplies = this.container.querySelector('#ta-quick-replies') as HTMLElement;
    if (quickReplies) {
      quickReplies.style.display = 'none';
    }
  }

  private setupEventListeners(): void {
    // FAB Toggle
    this.fab.addEventListener('click', () => this.toggle());

    // Close Button
    const closeBtn = this.container.querySelector('#ta-close-btn')!;
    closeBtn.addEventListener('click', () => this.close());

    // Escape-Taste zum Schließen des Chats
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Sound Toggle
    const soundBtn = this.container.querySelector('#ta-sound-toggle')!;
    soundBtn.addEventListener('click', () => {
      this.soundEnabled = !this.soundEnabled;
      soundBtn.classList.toggle('ta-sound-off', !this.soundEnabled);
      soundBtn.setAttribute('title', this.soundEnabled ? 'Ton ausschalten' : 'Ton einschalten');
    });

    // Send on button or enter
    this.sendButton.addEventListener('click', () => this.handleSendMessage());
    this.inputField.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSendMessage();
      }
    });

    // Quick Reply Chips
    this.quickRepliesContainer.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('ta-chip-btn')) {
        const val = target.dataset.val || target.textContent || '';
        this.inputField.value = val;
        this.handleSendMessage();
      }
    });

    // Delegierter Klick auf Buttons in Nachrichten (z.B. Quick-Replies in Bot-Nachrichten)
    this.messagesBody.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const quickBtn = target.closest('.ta-quick-btn') as HTMLElement;
      if (quickBtn && quickBtn.dataset.reply) {
        const replyText = quickBtn.dataset.reply;
        const parentReplies = quickBtn.closest('.ta-quick-replies');
        if (parentReplies) {
          parentReplies.querySelectorAll('button').forEach((b) => {
            (b as HTMLButtonElement).disabled = true;
            (b as HTMLElement).style.pointerEvents = 'none';
            (b as HTMLElement).style.opacity = '0.5';
          });
        }
        this.inputField.value = replyText;
        this.handleSendMessage();
        return;
      }

      const rebookBtn = target.closest('.ta-rebook-btn') as HTMLButtonElement;
      if (rebookBtn) {
        rebookBtn.disabled = true;
        rebookBtn.style.opacity = '0.5';
        this.inputField.value = 'Ich möchte einen neuen Termin vereinbaren';
        this.handleSendMessage();
      }
    });

    // File/Photo Upload mit automatischer Client-Side Skalierung & Komprimierung
    this.fileInput.addEventListener('change', async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          const compressed = await this.compressImage(file);
          this.attachedPhotoBase64 = compressed;
          this.photoThumbnail.src = compressed;
          this.photoInfo.textContent = file.name;
          this.photoPreviewBar.classList.add('ta-visible');
        } catch (err) {
          console.error('Fehler beim Einlesen des Fotos:', err);
        }
      }
    });

    // Remove photo
    const removePhotoBtn = this.container.querySelector('#ta-photo-remove')!;
    removePhotoBtn.addEventListener('click', () => {
      this.clearPhoto();
    });

    // Voice-In Diktierfunktion via Web Speech API
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      try {
        this.speechRecognition = new SpeechRecognition();
        this.speechRecognition.lang = 'de-DE';
        this.speechRecognition.interimResults = true;
        this.speechRecognition.continuous = false;

        this.speechRecognition.onstart = () => {
          this.isRecordingVoice = true;
          this.voiceButton.classList.add('ta-recording');
          this.voiceButton.setAttribute('title', 'Sprachaufnahme aktiv... Klicken zum Stoppen');
        };

        this.speechRecognition.onresult = (event: any) => {
          let transcript = '';
          for (let i = event.resultIndex; i < event.results.length; ++i) {
            transcript += event.results[i][0].transcript;
          }
          if (transcript) {
            this.inputField.value = transcript;
          }
        };

        this.speechRecognition.onerror = (event: any) => {
          console.warn('[VoiceInput] Spracheingabe-Hinweis:', event.error);
          this.stopVoiceRecognition();
        };

        this.speechRecognition.onend = () => {
          this.stopVoiceRecognition();
        };

        this.voiceButton.addEventListener('click', () => {
          if (this.isRecordingVoice) {
            this.speechRecognition.stop();
          } else {
            try {
              this.speechRecognition.start();
            } catch (e) {
              console.warn('[VoiceInput] Startfehler:', e);
            }
          }
        });
      } catch (err) {
        console.warn('[VoiceInput] Initialisierungsfehler:', err);
        if (this.voiceButton) this.voiceButton.style.display = 'none';
      }
    } else {
      if (this.voiceButton) {
        this.voiceButton.style.display = 'none';
      }
    }
  }

  private stopVoiceRecognition(): void {
    this.isRecordingVoice = false;
    if (this.voiceButton) {
      this.voiceButton.classList.remove('ta-recording');
      this.voiceButton.setAttribute('title', 'Spracheingabe / Diktieren');
    }
  }

  private compressImage(file: File, maxWidth = 1200, maxHeight = 1200, quality = 0.8): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            } else {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(event.target?.result as string);
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = () => resolve(event.target?.result as string);
        img.src = event.target?.result as string;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  private clearPhoto(): void {
    this.attachedPhotoBase64 = null;
    this.photoPreviewBar.classList.remove('ta-visible');
    this.fileInput.value = '';
  }

  public open(): void {
    this.isOpen = true;
    this.container.classList.add('ta-chat-active');
    this.chatWindow.classList.add('ta-open');
    this.chatWindow.setAttribute('aria-hidden', 'false');
    this.inputField.focus();
    this.scrollToBottom();
  }

  public close(): void {
    this.isOpen = false;
    this.container.classList.remove('ta-chat-active');
    this.chatWindow.classList.remove('ta-open');
    this.chatWindow.setAttribute('aria-hidden', 'true');
  }

  public toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  public sendDirectMessage(text: string): void {
    this.open();
    if (this.inputField) {
      this.inputField.value = text;
      this.handleSendMessage();
    }
  }

  private setBookingCookie(booking: { bookingId: string; displayDate?: string; startTime: string; serviceType?: string }): void {
    try {
      const value = JSON.stringify(booking);
      localStorage.setItem('termin_agent_booking', value);
      const maxAge = 30 * 24 * 60 * 60;
      document.cookie = `termin_agent_booking=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
    } catch (e) {
      console.warn('[TerminAgent] Cookie write failed:', e);
    }
  }

  private getBookingCookie(): { bookingId: string; displayDate?: string; startTime: string; serviceType?: string } | null {
    try {
      const local = localStorage.getItem('termin_agent_booking');
      if (local) return JSON.parse(local);

      const match = document.cookie.match(/(?:^|;\s*)termin_agent_booking=([^;]+)/);
      if (match) {
        return JSON.parse(decodeURIComponent(match[1]));
      }
    } catch (e) {
      console.warn('[TerminAgent] Cookie read failed:', e);
    }
    return null;
  }

  private clearBookingCookie(): void {
    try {
      localStorage.removeItem('termin_agent_booking');
      document.cookie = 'termin_agent_booking=; path=/; max-age=0; SameSite=Lax';
    } catch (e) {
      console.warn('[TerminAgent] Cookie clear failed:', e);
    }
  }

  private renderInitialMessage(): void {
    const saved = this.getBookingCookie();
    if (saved && saved.bookingId) {
      const tenantParam = this.config.tenantId ? `&tenantId=${encodeURIComponent(this.config.tenantId)}` : '';
      fetch(`${this.config.apiUrl}/api/booking-status?bookingId=${encodeURIComponent(saved.bookingId)}${tenantParam}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data && data.active && !data.isPast && data.booking) {
            const b = data.booking;
            const startDate = new Date(b.startTime);
            const dateStr = startDate.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' });
            const timeStr = startDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
            this.appendBotMessage(
              `Willkommen zurück bei **${this.config.businessName}**.\n\nSie haben bereits einen bestätigten Termin bei uns:\n\n**${dateStr} um ${timeStr} Uhr**\nBuchungs-ID: \`${b.bookingId}\`\n\nWas möchten Sie tun?`,
              {
                type: 'quickReplies',
                data: [
                  { label: 'Termin verschieben', value: 'Ich möchte meinen Termin verschieben' },
                  { label: 'Termin stornieren', value: 'Ich möchte meinen Termin stornieren' },
                  { label: 'Weiteren Termin buchen', value: 'Ich möchte einen weiteren Termin buchen' },
                ],
              }
            );
          } else {
            this.clearBookingCookie();
            this.renderDefaultInitialMessage();
          }
        })
        .catch(() => {
          this.renderDefaultInitialMessage();
        });
    } else {
      this.renderDefaultInitialMessage();
    }
  }

  private renderDefaultInitialMessage(): void {
    if (this.isPaused) {
      this.appendBotMessage(`**Hinweis zur Erreichbarkeit**\n\n${this.pausedMessage || 'Unser digitaler Terminservice pausiert vorübergehend. Bitte kontaktieren Sie uns direkt telefonisch.'}`);
      return;
    }
    const welcomeText = this.welcomeMessage || `Guten Tag und herzlich willkommen bei **${this.config.businessName}**.\n\nIch bin Ihr digitaler Terminkoordinator. Gerne helfe ich Ihnen, den passenden Termin für Ihr Anliegen zu finden. Wie kann ich Ihnen behilflich sein?`;
    this.appendBotMessage(welcomeText);
  }

  private async handleSendMessage(customContext?: Record<string, any>): Promise<void> {
    if (this.isSending || this.conversationEnded) return;
    if (this.isPaused) {
      this.appendBotMessage(`Der Terminservice ist derzeit pausiert. Bitte wenden Sie sich telefonisch an ${this.config.emergencyNumber}.`);
      return;
    }
    const text = this.inputField.value.trim();
    if (!text && !this.attachedPhotoBase64) return;
    if (text.length > 2000) {
      this.appendBotMessage('Bitte kürzen Sie Ihre Nachricht auf höchstens 2.000 Zeichen.');
      return;
    }

    const history = this.messages.slice(-24).map((m) => ({ role: m.role, content: m.content.slice(0, 2500) }));
    this.isSending = true;
    this.sendButton.setAttribute('disabled', 'true');

    this.inputField.value = '';
    const photoPayload = this.attachedPhotoBase64;
    this.clearPhoto();

    // User Message rendern
    this.appendUserMessage(text, photoPayload);

    // Typing Indicator einblenden
    this.showTypingIndicator();

    try {
      const saved = this.getBookingCookie();

      const res = await fetch(`${this.config.apiUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Chat-Session': this.chatSessionId },
        body: JSON.stringify({
          tenantId: this.config.tenantId,
          message: text,
          history,
          context: {
            tenantId: this.config.tenantId,
            attachedPhotoUrl: photoPayload || undefined,
            cookieBookingId: saved?.bookingId,
            ...customContext,
          },
        }),
      });

      this.removeTypingIndicator();

      const data = await res.json();
      if (!res.ok && !data.reply) throw new Error('Server-Antwort fehlerhaft');
      this.appendBotMessage(data.reply, data.uiPayload);
      if (data.conversationEnded) {
        this.conversationEnded = true;
        this.inputField.disabled = true;
        this.inputField.placeholder = 'Bitte kontaktieren Sie den Betrieb telefonisch';
      }

      if (data.emergencyTriggered || data.uiPayload?.type === 'emergency') {
        this.triggerEmergencyBanner(data.uiPayload?.data?.phone || this.config.emergencyNumber);
      }
    } catch (err) {
      this.removeTypingIndicator();
      this.appendBotMessage(
        `Entschuldigung, es liegt aktuell ein kurzes Verbindungsproblem vor. Bitte versuchen Sie es gleich noch einmal oder erreichen Sie uns direkt telefonisch unter ${this.config.emergencyNumber}.`
      );
      this.triggerEmergencyBanner(this.config.emergencyNumber);
    } finally {
      this.isSending = false;
      if (!this.conversationEnded) this.sendButton.removeAttribute('disabled');
    }
  }

  private appendUserMessage(text: string, photoBase64: string | null = null): void {
    const time = this.getFormattedTime();
    const msgId = `msg-${Date.now()}`;

    let contentHtml = this.formatMarkdown(text);
    if (photoBase64) {
      contentHtml += `<div style="margin-top:8px;"><img src="${photoBase64}" style="max-width:180px;border-radius:8px;border:1px solid rgba(255,255,255,0.4);" alt="Foto" /></div>`;
    }

    const msgEl = document.createElement('div');
    msgEl.className = 'ta-message ta-user';
    msgEl.innerHTML = `
      <div class="ta-msg-content">
        <div>${contentHtml}</div>
        <div class="ta-msg-time">${time}</div>
      </div>
    `;

    this.messagesBody.appendChild(msgEl);
    this.messages.push({
      id: msgId,
      role: 'user',
      content: text,
      timestamp: time,
    });

    this.scrollToBottom();
  }

  private appendBotMessage(text: string, uiPayload?: any): void {
    const time = this.getFormattedTime();
    const msgId = `msg-${Date.now()}`;
    let extraHtml = '';

    // Buchungsbestätigung rendern
    if (uiPayload?.type === 'booking' && uiPayload.data) {
      if (uiPayload.action !== 'past') {
        this.setBookingCookie({
          bookingId: uiPayload.data.bookingId,
          startTime: uiPayload.data.startTime,
          displayDate: uiPayload.data.confirmationMessage,
          serviceType: uiPayload.data.details?.serviceType,
        });
      }

      // Alle bisherigen Kalender im Chatverlauf deaktivieren und ausgrauen
      this.disableAllPastCalendarPickers(uiPayload.data.startTime);

      const badgeText =
        uiPayload.action === 'rescheduled'
          ? 'Termin umgebucht'
          : uiPayload.action === 'past'
          ? 'Vergangener / Abgeschlossener Termin'
          : uiPayload.action === 'found'
          ? 'Aktiver Termin im System'
          : 'Verbindlich gebucht';

      extraHtml += `
        <div class="ta-booking-card">
          <div class="ta-booking-badge">${badgeText}</div>
          <div style="font-weight:bold;font-size:13.5px;">${uiPayload.data.confirmationMessage}</div>
          <div style="font-size:11.5px;color:var(--ta-text-muted);">Buchungs-ID: ${uiPayload.data.bookingId}</div>
          
          <div class="ta-cal-sync-group">
            <div class="ta-cal-sync-title">Termin im Kalender speichern:</div>
            <div class="ta-cal-sync-buttons">
              ${
                uiPayload.data.googleCalendarUrl
                  ? `<a href="${uiPayload.data.googleCalendarUrl}" target="_blank" rel="noopener noreferrer" class="ta-cal-sync-btn ta-cal-google" title="In Google Kalender öffnen">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                      Google Kalender
                    </a>`
                  : ''
              }
              <a href="${uiPayload.data.icsDownloadUrl || `${this.config.apiUrl}/api/booking/${uiPayload.data.bookingId}/ics`}" download="termin-${uiPayload.data.bookingId}.ics" class="ta-cal-sync-btn ta-cal-ics" title="Apple Kalender, Outlook oder .ics herunterladen">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Apple / Outlook (.ics)
              </a>
            </div>
          </div>

          ${
            uiPayload.action === 'found'
              ? `
          <div class="ta-quick-replies" style="margin-top:10px;justify-content:flex-start;gap:6px;">
            <button class="ta-quick-btn" data-reply="Ich möchte meinen Termin verschieben" style="font-size:11.5px;padding:4px 8px;">Termin verschieben</button>
            <button class="ta-quick-btn" data-reply="Ich möchte meinen Termin stornieren" style="font-size:11.5px;padding:4px 8px;color:#ef4444;border-color:#fca5a5;">Termin stornieren</button>
            <button class="ta-quick-btn" data-reply="Ich möchte einen weiteren Termin buchen" style="font-size:11.5px;padding:4px 8px;">Weiterer Termin</button>
          </div>
          `
              : uiPayload.action === 'past'
              ? `
          <div class="ta-quick-replies" style="margin-top:10px;justify-content:flex-start;gap:6px;">
            <button class="ta-quick-btn" data-reply="Ich möchte einen neuen Termin vereinbaren" style="font-size:11.5px;padding:4px 8px;">Neuen Termin vereinbaren</button>
          </div>
          `
              : ''
          }
        </div>
      `;
    }

    // Stornierungsbestätigung rendern
    if (uiPayload?.type === 'cancellation') {
      this.clearBookingCookie();
      this.disableAllPastCalendarPickers();

      const bookingId = uiPayload.data?.bookingId || '';
      const emailSent = uiPayload.data?.emailSent !== false;

      extraHtml += `
        <div class="ta-cancellation-card">
          <div class="ta-cancellation-badge">Termin storniert</div>
          <div style="font-weight:600;font-size:13px;color:#ef4444;">Der Termin wurde aus unserem Kalender gelöscht und der Slot freigegeben.</div>
          ${bookingId ? `<div style="font-size:11.5px;color:var(--ta-text-muted);">Buchungs-ID: <code>${bookingId}</code></div>` : ''}
          ${emailSent ? `
          <div class="ta-cancellation-email-note">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-2px;margin-right:4px;"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            Bestätigungs-E-Mail mit Link zur Neu-Terminierung wurde versandt
          </div>` : ''}
          <button class="ta-rebook-btn" type="button">Neuen Wunschtermin wählen</button>
        </div>
      `;
    }

    const hasSlots = Boolean(uiPayload?.type === 'slots' && uiPayload.data?.slots && uiPayload.data.slots.length > 0);
    if (hasSlots) {
      // Ältere Kalender im Chat deaktivieren
      this.disableAllPastCalendarPickers();
    }
    const msgEl = document.createElement('div');
    msgEl.className = `ta-message ta-bot${hasSlots ? ' ta-has-calendar-msg' : ''}`;
    msgEl.innerHTML = `
      <div class="ta-msg-content" style="${hasSlots ? 'width:100%;max-width:100%;' : ''}">
        <div>${this.formatMarkdown(text)}</div>
        ${extraHtml}
        <div class="ta-msg-time">${time}</div>
      </div>
    `;

    // Interaktiven Cal.com Monatskalender mit Zeitspalte einbinden
    if (hasSlots) {
      const contentEl = msgEl.querySelector('.ta-msg-content') as HTMLElement;
      this.renderInteractiveMonthCalendar(
        contentEl,
        uiPayload.data.slots,
        uiPayload.data.serviceType,
        uiPayload.data.isRescheduling,
        uiPayload.data.oldBookingId,
        uiPayload.data.absenceInfo
      );
    }

    this.messagesBody.appendChild(msgEl);
    this.messages.push({
      id: msgId,
      role: 'assistant',
      content: text,
      timestamp: time,
      uiPayload,
    });

    this.playChimeSound();
    if (hasSlots) {
      requestAnimationFrame(() => {
        msgEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } else {
      this.scrollToBottom();
    }
  }

  /**
   * Rendert den interaktiven Cal.com Monatskalender mit Zeitspalte
   */
  private renderInteractiveMonthCalendar(
    msgContentEl: HTMLElement,
    slots: any[],
    serviceType?: string,
    isRescheduling?: boolean,
    oldBookingId?: string,
    absenceInfo?: { isAbsent: boolean; reason?: string; fromDate?: string; toDate?: string; nextAvailableDate?: string }
  ): void {
    this.chatWindow.classList.add('ta-has-calendar');

    // Slots nach YYYY-MM-DD gruppieren
    const slotsByDate: Map<string, any[]> = new Map();
    for (const slot of slots) {
      const dateKey = slot.startTime.slice(0, 10);
      if (!slotsByDate.has(dateKey)) {
        slotsByDate.set(dateKey, []);
      }
      slotsByDate.get(dateKey)!.push(slot);
    }

    const availableDateKeys = Array.from(slotsByDate.keys()).sort();
    let selectedDateKey = availableDateKeys[0] || new Date().toISOString().slice(0, 10);

    // Initiales Jahr / Monat
    let displayYear = parseInt(selectedDateKey.slice(0, 4), 10);
    let displayMonth = parseInt(selectedDateKey.slice(5, 7), 10) - 1; // 0-11

    let isCompleted = false;
    let chosenSlotTime: string | null = null;

    const pickerEl = document.createElement('div');
    pickerEl.className = 'ta-calendar-picker';

    const render = () => {
      const monthNames = [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
      ];
      const weekdayNames = ['SO', 'MO', 'DI', 'MI', 'DO', 'FR', 'SA']; // Wie im Referenzbild!

      if (isCompleted) {
        pickerEl.classList.add('ta-disabled');
      }

      const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
      const firstDayOfWeek = new Date(displayYear, displayMonth, 1).getDay(); // 0 = So, 1 = Mo ...

      let daysHtml = '';
      for (let i = 0; i < firstDayOfWeek; i++) {
        daysHtml += `<div class="ta-cal-day-cell ta-empty"></div>`;
      }

      const todayStr = new Date().toISOString().slice(0, 10);

      for (let day = 1; day <= daysInMonth; day++) {
        const mm = String(displayMonth + 1).padStart(2, '0');
        const dd = String(day).padStart(2, '0');
        const dateKey = `${displayYear}-${mm}-${dd}`;

        const daySlots = slotsByDate.get(dateKey) || [];
        const hasSlots = daySlots.length > 0;
        const isSelected = dateKey === selectedDateKey;
        const isToday = dateKey === todayStr;

        let cellClass = 'ta-cal-day-cell';
        if (hasSlots && !isCompleted) cellClass += ' ta-has-slots';
        if (isSelected) cellClass += ' ta-selected';
        if (isToday) cellClass += ' ta-is-today';

        daysHtml += `<div class="${cellClass}" data-date-key="${dateKey}">${day}</div>`;
      }

      // Zeiten für den aktuell ausgewählten Tag
      const selectedDaySlots = slotsByDate.get(selectedDateKey) || [];
      const selectedDateObj = new Date(selectedDateKey);
      const selectedDayDisplay = selectedDateObj.toLocaleDateString('de-DE', {
        weekday: 'short',
        day: 'numeric',
      });

      let timesHtml = '';
      if (selectedDaySlots.length > 0) {
        for (const slot of selectedDaySlots) {
          const startTime = new Date(slot.startTime).toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
          });
          const slotIsoMinute = new Date(slot.startTime).toISOString().slice(0, 16);
          const chosenIsoMinute = chosenSlotTime ? new Date(chosenSlotTime).toISOString().slice(0, 16) : null;
          const isChosen = isCompleted && chosenIsoMinute === slotIsoMinute;
          timesHtml += `
            <button class="ta-cal-time-btn${isChosen ? ' ta-chosen-slot' : ''}" ${isCompleted ? 'disabled' : ''} data-slot-id="${slot.id}" data-slot-time="${slot.startTime}" data-slot-display="${slot.displayDate}">
              ${startTime}
            </button>
          `;
        }
      } else {
        timesHtml = `<div style="font-size:12px;color:#94a3b8;padding:12px 0;">Keine Termine verfügbar.</div>`;
      }

      pickerEl.innerHTML = `
        <div class="ta-cal-header-bar">
          <div class="ta-cal-service-info">
            <span>${serviceType || 'Terminauswahl'}</span>
          </div>
          ${
            isCompleted
              ? `<span class="ta-cal-booked-badge">Termin ausgewählt</span>`
              : `<span class="ta-cal-duration-chip">${this.slotDurationMinutes} Min &bull; ${this.serviceLocationMode === 'on_premise' ? 'In der Praxis' : (this.serviceLocationMode === 'remote' ? 'Online / Tel.' : 'Vor Ort')}</span>`
          }
        </div>

        ${
          absenceInfo?.isAbsent
            ? `
          <div class="ta-cal-absence-alert">
            <span style="font-weight:700;">${absenceInfo.reason || 'Abwesenheit'}:</span>
            <span>Bis ${absenceInfo.toDate} &bull; Termine ab ${absenceInfo.nextAvailableDate}</span>
          </div>
        `
            : ''
        }

        <div class="ta-cal-body">
          <!-- Links: Monatskalender -->
          <div class="ta-cal-month-col">
            <div class="ta-cal-month-nav">
              <span class="ta-cal-month-title">${monthNames[displayMonth]} ${displayYear}</span>
              <div class="ta-cal-nav-buttons">
                <button class="ta-cal-nav-btn ta-cal-prev" ${isCompleted ? 'disabled' : ''} title="Vorheriger Monat">&lsaquo;</button>
                <button class="ta-cal-nav-btn ta-cal-next" ${isCompleted ? 'disabled' : ''} title="Nächster Monat">&rsaquo;</button>
              </div>
            </div>

            <div class="ta-cal-weekdays">
              ${weekdayNames.map((w) => `<div class="ta-cal-weekday">${w}</div>`).join('')}
            </div>

            <div class="ta-cal-days-grid">
              ${daysHtml}
            </div>
          </div>

          <!-- Rechts: Zeitspalte -->
          <div class="ta-cal-times-col">
            <div class="ta-cal-times-header">
              <span class="ta-cal-times-day-title">${selectedDayDisplay}</span>
              <span class="ta-cal-times-badge">24 Std</span>
            </div>
            <div class="ta-cal-times-list">
              ${timesHtml}
            </div>
          </div>
        </div>
      `;

      // Navigation
      pickerEl.querySelector('.ta-cal-prev')?.addEventListener('click', (e) => {
        e.stopPropagation();
        if (isCompleted) return;
        if (displayMonth === 0) {
          displayMonth = 11;
          displayYear--;
        } else {
          displayMonth--;
        }
        render();
      });

      pickerEl.querySelector('.ta-cal-next')?.addEventListener('click', (e) => {
        e.stopPropagation();
        if (isCompleted) return;
        if (displayMonth === 11) {
          displayMonth = 0;
          displayYear++;
        } else {
          displayMonth++;
        }
        render();
      });

      // Tage-Klick
      pickerEl.querySelectorAll('.ta-cal-day-cell.ta-has-slots').forEach((cell) => {
        cell.addEventListener('click', () => {
          if (isCompleted) return;
          const dateKey = (cell as HTMLElement).dataset.dateKey;
          if (dateKey) {
            selectedDateKey = dateKey;
            render();
          }
        });
      });

      // Zeit-Slot Klick
      pickerEl.querySelectorAll('.ta-cal-time-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (isCompleted) return;
          const slotId = (btn as HTMLElement).dataset.slotId;
          const slotTime = (btn as HTMLElement).dataset.slotTime;
          const slotDisplay = (btn as HTMLElement).dataset.slotDisplay;
          const startTimeStr = btn.textContent?.trim() || '';

          isCompleted = true;
          chosenSlotTime = slotTime || null;
          render();

          this.inputField.value = isRescheduling
            ? `Ich buche um auf den Termin am ${slotDisplay} um ${startTimeStr} Uhr.`
            : `Ich wähle den Termin am ${slotDisplay} um ${startTimeStr} Uhr.`;
          this.handleSendMessage({
            selectedSlot: { id: slotId, time: slotTime, display: slotDisplay },
            isRescheduling,
            oldBookingId,
          });
        });
      });
    };

    render();
    msgContentEl.appendChild(pickerEl);
  }

  /**
   * Deaktiviert und graut alle Kalender-Auswahlen im Chatverlauf aus
   */
  private disableAllPastCalendarPickers(bookedStartTime?: string): void {
    const pickers = this.messagesBody.querySelectorAll('.ta-calendar-picker');
    const bookedIso = bookedStartTime ? new Date(bookedStartTime).toISOString().slice(0, 16) : null;

    pickers.forEach((picker) => {
      picker.classList.add('ta-disabled');
      picker.querySelectorAll('button').forEach((b) => {
        (b as HTMLButtonElement).disabled = true;
      });

      const headerBar = picker.querySelector('.ta-cal-header-bar');
      if (headerBar) {
        let badge = headerBar.querySelector('.ta-cal-booked-badge');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'ta-cal-booked-badge';
          headerBar.appendChild(badge);
        }
        badge.textContent = bookedStartTime ? 'Termin gebucht' : 'Auswahl beendet';
        const durationChip = headerBar.querySelector('.ta-cal-duration-chip');
        if (durationChip) {
          (durationChip as HTMLElement).style.display = 'none';
        }
      }

      if (bookedIso) {
        picker.querySelectorAll('.ta-cal-time-btn').forEach((btn) => {
          const sTime = (btn as HTMLElement).dataset.slotTime;
          if (sTime) {
            const slotIso = new Date(sTime).toISOString().slice(0, 16);
            if (slotIso === bookedIso) {
              btn.classList.add('ta-chosen-slot');
            }
          }
        });
      }
    });
  }

  private triggerEmergencyBanner(phone?: string): void {
    this.emergencyBanner.classList.add('ta-active');
    if (phone) {
      this.emergencyPhoneLink.href = `tel:${phone}`;
      this.emergencyPhoneLink.textContent = `Notruf: ${phone}`;
    }
  }

  private showTypingIndicator(): void {
    if (document.getElementById('ta-typing')) return;

    const typingEl = document.createElement('div');
    typingEl.id = 'ta-typing';
    typingEl.className = 'ta-message ta-bot';
    typingEl.innerHTML = `
      <div class="ta-typing-bubble">
        <span class="ta-typing-dot"></span>
        <span class="ta-typing-dot"></span>
        <span class="ta-typing-dot"></span>
      </div>
    `;
    this.messagesBody.appendChild(typingEl);
    this.scrollToBottom();
  }

  private removeTypingIndicator(): void {
    const el = document.getElementById('ta-typing');
    if (el) el.remove();
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      this.messagesBody.scrollTop = this.messagesBody.scrollHeight;
    });
  }

  private getFormattedTime(): string {
    return new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  /**
   * Einfaches, sicheres Markdown-Parsing für fette Schrift, Zeilenumbrüche und Links
   */
  private formatMarkdown(text: string): string {
    if (!text) return '';
    let parsed = this.escapeHtml(text);
    // Bold: **text**
    parsed = parsed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Line breaks
    parsed = parsed.replace(/\n/g, '<br/>');
    return parsed;
  }

  private escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Sound-Feedback via nativer Web Audio API Synthesizer (Zero-Dependency Chime)
   */
  private playChimeSound(): void {
    if (!this.soundEnabled) return;
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      const ctx = new AudioContextClass();

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15); // A5

      gain.gain.setValueAtTime(0.04, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch {
      // Ignorieren, falls Browser Audio-Policies blockieren
    }
  }
}

// Auto-Mount bei direkter Script-Einbettung
if (typeof window !== 'undefined') {
  let widgetInstance: TerminAgentWidget | null = null;

  (window as any).TerminAgent = {
    init: (config?: WidgetConfig) => {
      widgetInstance = new TerminAgentWidget(config);
      (window as any).TerminAgent.instance = widgetInstance;
      return widgetInstance;
    },
    open: () => widgetInstance?.open(),
    close: () => widgetInstance?.close(),
    toggle: () => widgetInstance?.toggle(),
    sendDirectMessage: (text: string) => widgetInstance?.sendDirectMessage(text),
    setTheme: (theme: string) => widgetInstance?.setTheme(theme),
    getTheme: () => widgetInstance?.getTheme(),
    get instance() {
      return widgetInstance;
    },
  };

  window.addEventListener('message', (event) => {
    if (event.data?.type === 'TA_SIMULATE' && typeof event.data.text === 'string') {
      if (widgetInstance) {
        widgetInstance.sendDirectMessage(event.data.text);
      }
    }
  });

  // Automatischer Start, falls per <script> eingebunden
  window.addEventListener('DOMContentLoaded', () => {
    const existingRoot = document.getElementById('termin-agent-root');
    if (!existingRoot) {
      widgetInstance = new TerminAgentWidget();
      (window as any).TerminAgent.instance = widgetInstance;
    }
  });
}
